import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { loginWithEmail } from "../../services/service";
import { CustomToast } from "../../atoms/toastMessage";
import { normalizePlanName } from "../../utils/planFeatureAccess";

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await loginWithEmail(credentials);

      if (response?.data) {
        const userRole = response?.data?.user?.role?.toLowerCase();
        
        // Check if user role is valid (manager or super_admin)
        if (userRole !== 'manager' && userRole !== 'super_admin') {
          const errorMsg = "Only managers and super admins are allowed to access this portal";
          CustomToast({ 
            type: "error", 
            message: errorMsg 
          });
          return rejectWithValue(errorMsg);
        }

        localStorage.setItem("token", response?.data?.token);
        const userPlan = normalizePlanName(
          response?.data?.user?.OrganizationUsers?.OrganizationPlan?.plan_name || "trial"
        );
        
        CustomToast({
          type: "success",
          message: `${response?.data?.user?.role} is signed in`,
        });
        return {
          user: response.data,
          token: response?.data?.token,
          plan: userPlan
        };
      } else {
        const errorMsg = "Invalid response from server. Please try again.";
        CustomToast({ 
          type: "error", 
          message: errorMsg 
        });
        return rejectWithValue(errorMsg);
      }
    } catch (error) {
      const errorMessage = error.message || "Invalid Email or Password";
      CustomToast({ 
        type: "error", 
        message: errorMessage 
      });
      return rejectWithValue(errorMessage);
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    token: null,
    currentPlan: 'trial' // Default plan
  },
  reducers: {
    clearAuthentication: (state) => {
      state.user = null;
      state.token = null;
      state.currentPlan = 'trial';
    },
    setPlan: (state, action) => {
      state.currentPlan = normalizePlanName(action.payload);
    },
    initializeToken: (state) => {
      const token = localStorage.getItem("token");
      const userDataString = localStorage.getItem("userData");
      if (token) {
        state.token = token;
      }
      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          state.user = userData;
        } catch (error) {
          console.error('Error parsing userData from localStorage:', error);
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.currentPlan = action.payload.plan;
      })
      .addCase(loginUser.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.currentPlan = 'trial';
      });
  },
});

export const { clearAuthentication, setPlan, initializeToken } = authSlice.actions;

export default authSlice.reducer;
